import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './FeedbackModal.css';

const CATEGORIES = ['App Experience', 'Property Listings', 'Search & Filters', 'Map & Radar', 'Listing Process', 'Customer Support'];

const QUICK_TAGS = [
  '👍 Easy to use', '🏠 Good listings', '⚡ Fast search', '📞 Direct owner contact',
  '💰 Saves money', '📍 Great map', '🎨 Better design needed', '🏙 More cities needed',
  '📸 More photos needed', '🔍 More filters needed', '💬 Quick support', '📄 Free agreement helpful'
];

export default function FeedbackModal({ onClose, triggerSource = 'general' }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: rating, 2: details, 3: done
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [categories, setCategories] = useState(
    triggerSource === 'listing' ? ['Listing Process'] : ['App Experience']
  );
  const [selectedTags, setSelectedTags] = useState([]);
  const [message, setMessage] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Resolve best available name — never fall back to 'Anonymous'
  const resolvedName = user?.name ||
    (user?.email
      ? user.email.split('@')[0].replace(/[._-]/g, ' ')
          .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : '');

  const RATING_LABELS = ['', 'Poor 😕', 'Fair 😐', 'Good 🙂', 'Very Good 😊', 'Excellent 🤩'];

  const toggleCategory = (c) => {
    setCategories(prev =>
      prev.includes(c) ? (prev.length > 1 ? prev.filter(x => x !== c) : prev) : [...prev, c]
    );
  };

  const toggleTag = (t) => {
    setSelectedTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { setError('Please write a short message.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.post('/api/support/feedback', {
        name: resolvedName || nameInput.trim() || 'Spaczo User',
        email: user?.email || '',
        rating,
        category: categories.join(', '),
        tags: selectedTags.join(', '),
        message,
        source: triggerSource,
        is_verified: !!user,
      });
      setStep(3);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fb-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fb-modal">
        <button className="fb-modal-close" onClick={onClose} title="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {step === 3 ? (
          <div className="fb-modal-done">
            <div className="fb-done-icon">🎉</div>
            <h3>Thanks for your feedback!</h3>
            <p>Your input helps us build a better experience for everyone.</p>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : step === 1 ? (
          <div className="fb-modal-step">
            <div className="fb-modal-header">
              <div className="fb-modal-emoji">{triggerSource === 'listing' ? '🚀' : '👋'}</div>
              <h3>{triggerSource === 'listing' ? 'How was the listing experience?' : 'Enjoying Spaczo?'}</h3>
              <p>{triggerSource === 'listing' ? 'Your property is live! How easy was it to list?' : 'Take 30 seconds to share your experience.'}</p>
            </div>

            <div className="fb-stars-row">
              {[1,2,3,4,5].map(s => (
                <button key={s} type="button" className="fb-star"
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => { setRating(s); setStep(2); }}>
                  <svg width="40" height="40" viewBox="0 0 24 24"
                    fill={s <= (hover || rating) ? '#f59e0b' : 'none'}
                    stroke={s <= (hover || rating) ? '#f59e0b' : '#d1d5db'} strokeWidth="1.5">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                </button>
              ))}
            </div>
            {(hover || rating) > 0 && <div className="fb-rating-hint">{RATING_LABELS[hover || rating]}</div>}
            <button className="fb-skip" onClick={onClose}>Maybe later</button>
          </div>
        ) : (
          <form className="fb-modal-step" onSubmit={handleSubmit}>
            <div className="fb-modal-header">
              <div className="fb-stars-display">
                {[1,2,3,4,5].map(s => (
                  <svg key={s} width="20" height="20" viewBox="0 0 24 24"
                    fill={s <= rating ? '#f59e0b' : 'none'}
                    stroke={s <= rating ? '#f59e0b' : '#d1d5db'} strokeWidth="1.5"
                    style={{ cursor: 'pointer' }} onClick={() => setRating(s)}>
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                ))}
                <span className="fb-rating-sm">{RATING_LABELS[rating]}</span>
              </div>
              <h3>Tell us more</h3>
            </div>

            {/* Multi-select categories */}
            <div className="fb-section-label">What's this about? <span className="fb-multi-hint">(select all that apply)</span></div>
            <div className="fb-category-pills">
              {CATEGORIES.map(c => (
                <button key={c} type="button"
                  className={`fb-pill ${categories.includes(c) ? 'active' : ''}`}
                  onClick={() => toggleCategory(c)}>
                  {categories.includes(c) && <span className="fb-pill-check">✓ </span>}
                  {c}
                </button>
              ))}
            </div>

            {/* Quick tags */}
            <div className="fb-section-label">Quick feedback <span className="fb-multi-hint">(optional)</span></div>
            <div className="fb-tags-wrap">
              {QUICK_TAGS.map(t => (
                <button key={t} type="button"
                  className={`fb-tag ${selectedTags.includes(t) ? 'active' : ''}`}
                  onClick={() => toggleTag(t)}>
                  {t}
                </button>
              ))}
            </div>

            {!resolvedName && (
              <input
                className="fb-name-input"
                type="text"
                placeholder="Your name (optional)"
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                maxLength={60}
              />
            )}

            <textarea
              className="fb-textarea"
              placeholder={rating >= 4
                ? 'What did you love? Any suggestions to make it even better?'
                : 'What went wrong? How can we improve?'}
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              required
            />

            {error && <div className="fb-error">{error}</div>}

            <div className="fb-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Sending...</> : 'Submit Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
